/**
 * 公共 API 响应拦截器
 * 前台和后台共用的响应解包和错误处理逻辑
 */
import type { AxiosResponse, AxiosError } from 'axios';
import { ApiError, type ApiResponse } from './types';

/**
 * 成功响应拦截器
 * 解包标准 API 响应格式 { error_code, message, data }
 */
export const responseInterceptor = (response: AxiosResponse<ApiResponse>) => {
  const apiResponse = response.data;

  // 检查是否是标准 API 响应格式
  if (apiResponse && typeof apiResponse === 'object' && 'error_code' in apiResponse) {
    if (apiResponse.error_code === 0) {
      // 分页响应：保留 meta 字段
      if ('meta' in apiResponse) {
        return { data: apiResponse.data, meta: (apiResponse as any).meta } as any;
      }
      // 普通响应：只返回 data
      return apiResponse.data as any;
    }
    // 业务错误
    throw new ApiError(
      apiResponse.message || '操作失败',
      apiResponse.error_code
    );
  }

  // 非标准格式，直接返回
  return apiResponse as any;
};

/**
 * 错误响应拦截器
 * 统一处理网络错误、超时、HTTP 状态码错误
 */
export const errorInterceptor = (error: AxiosError<ApiResponse>) => {
  // 网络错误或超时
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      throw new ApiError('网络请求超时，请检查网络连接', -1);
    }
    throw new ApiError('网络连接失败，请检查网络', -1);
  }

  const { status, data } = error.response;
  const message = data?.message || error.message || '请求失败';

  // 404 资源不存在
  if (status === 404) {
    throw new ApiError('请求的资源不存在', 404, status);
  }

  // 500+ 服务器错误
  if (status >= 500) {
    throw new ApiError('服务器错误，请稍后重试', status, status);
  }

  // 其他错误
  throw new ApiError(message, (data as any)?.error_code || status, status);
};
